document.addEventListener('DOMContentLoaded', () => {
    const initCarousel = (container) => {
        const track = container.querySelector('[data-js-carousel="track"]');
        const items = Array.from(container.querySelectorAll('[data-js-carousel="item"]'));

        if (!track && items.length === 0) return;

        const section = container;
        let nextButton = section.querySelector('[data-js-carousel="next"]');
        let prevButton = section.querySelector('[data-js-carousel="prev"]');
        let isAnimating = false;
        let scrollEndTimeout = null;
        let currentTargetIndex = -1;

        // --- Crossfade Mode (No Track - Used for Projects/Grid) ---
        if (!track && items.length > 0) {
            const isMobileQuery = window.matchMedia('(max-width: 1199px)');
            let currentIndex = 0;

            const syncAriaStates = () => {
                if (isMobileQuery.matches) {
                    // Mobile: Only the active one is visible to screen readers
                    items.forEach((item, i) => {
                        item.setAttribute('aria-hidden', i === currentIndex ? 'false' : 'true');
                    });
                } else {
                    // Desktop: Everything is visible to screen readers (Lighthouse fix)
                    items.forEach(item => item.removeAttribute('aria-hidden'));
                }
            };

            const updateActiveItem = (index) => {
                if (index < 0 || index >= items.length) return;
                currentIndex = index;
                syncAriaStates();
            };

            // Event Listeners for Crossfade
            isMobileQuery.addEventListener('change', syncAriaStates);
            syncAriaStates(); // Initial run

            const move = (direction) => {
                if (!isMobileQuery.matches) return;
                let nextIndex = direction === 'next'
                    ? (currentIndex + 1) % items.length
                    : (currentIndex - 1 + items.length) % items.length;
                updateActiveItem(nextIndex);
            };

            if (nextButton) nextButton.addEventListener('click', () => move('next'));
            if (prevButton) prevButton.addEventListener('click', () => move('prev'));

            // If there is no track, we stop here for this container
            return;
        }

        // --- Track Mode (Standard Carousel - Used for Services/Tabs) ---
        if (track) {
            const slides = Array.from(track.children);
            if (slides.length === 0) return;

            const getImages = () => {
                const panel = track.closest('[role="tabpanel"]');
                if (panel) return panel.querySelectorAll('[data-js-carousel="img"]');
                return section.querySelectorAll('[data-js-carousel="img"]');
            };

            const images = getImages();

            const isActive = () => {
                const panel = track.closest('[role="tabpanel"]');
                if (panel) return panel.getAttribute('aria-hidden') === 'false' || !panel.hasAttribute('hidden');
                return track.offsetParent !== null;
            };

            const clearAnimating = () => {
                isAnimating = false;
                currentTargetIndex = -1;
                if (scrollEndTimeout) clearTimeout(scrollEndTimeout);
                scrollEndTimeout = null;
            };

            const smoothScrollTo = (target) => {
                isAnimating = true;
                if (scrollEndTimeout) clearTimeout(scrollEndTimeout);
                track.scrollTo({ left: target, behavior: 'smooth' });
                scrollEndTimeout = setTimeout(clearAnimating, 600);
            };

            track.addEventListener('scrollend', clearAnimating);

            const observerOptions = {
                root: track,
                threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
            };

            const updateActiveState = (index) => {
                if (index < 0 || index >= slides.length) return;
                let stateChanged = false;

                slides.forEach((slide, i) => {
                    const link = slide.querySelector('.list__link') || slide.querySelector('a') || slide.querySelector('[role="tab"]');
                    if (link) {
                        if (i === index) {
                            if (link.getAttribute('role') === 'tab') {
                                if (link.getAttribute('aria-selected') !== 'true') {
                                    link.click();
                                    stateChanged = true;
                                }
                            } else {
                                if (link.getAttribute('aria-current') !== 'page') {
                                    link.setAttribute('aria-current', 'page');
                                    stateChanged = true;
                                }
                            }
                        } else {
                            if (link.hasAttribute('aria-current')) {
                                link.removeAttribute('aria-current');
                                stateChanged = true;
                            }
                        }
                    }
                });

                if (images.length > 1) {
                    images.forEach((el) => {
                        const elIndex = parseInt(el.getAttribute('data-index'));
                        if (!isNaN(elIndex)) {
                            const isVisible = elIndex === index;
                            el.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
                            el.setAttribute('tabindex', isVisible ? '0' : '-1');
                            stateChanged = true;
                        }
                    });
                }
                return stateChanged;
            };

            const intersectionRatios = new Map();
            const observer = new IntersectionObserver((entries) => {
                if (window.matchMedia('(min-width: 1200px)').matches) return;
                if (isAnimating || !isActive()) return;

                entries.forEach(entry => intersectionRatios.set(entry.target, entry.intersectionRatio));

                let bestSlide = null;
                let maxRatio = -1;
                const currentIndex = getActiveIndex();

                slides.forEach((slide, i) => {
                    const ratio = intersectionRatios.get(slide) || 0;
                    if (ratio > maxRatio + 0.01) {
                        maxRatio = ratio;
                        bestSlide = slide;
                    } else if (Math.abs(ratio - maxRatio) <= 0.01 && bestSlide !== null) {
                        if (i === currentIndex) bestSlide = slide;
                        else if (slides.indexOf(bestSlide) !== currentIndex) {
                            if (slide.offsetLeft < bestSlide.offsetLeft) bestSlide = slide;
                        }
                    }
                });

                if (bestSlide && maxRatio > 0.4) {
                    updateActiveState(slides.indexOf(bestSlide));
                }
            }, observerOptions);

            slides.forEach(slide => observer.observe(slide));

            let rafPending = false;
            const updateFadeVisibility = () => {
                if (rafPending) return;
                rafPending = true;
                requestAnimationFrame(() => {
                    const container = track.closest('.carousel__track-container');
                    if (container) {
                        const hasRightFade = track.scrollLeft + track.clientWidth < track.scrollWidth - 5;
                        container.classList.toggle('has-right-fade', hasRightFade);
                    }
                    rafPending = false;
                });
            };

            track.addEventListener('scroll', updateFadeVisibility);
            window.addEventListener('resize', updateFadeVisibility);
            updateFadeVisibility();

            const getActiveIndex = () => {
                const index = slides.findIndex(slide => {
                    const link = slide.querySelector('.list__link') || slide.querySelector('a') || slide.querySelector('[role="tab"]');
                    return link && (link.getAttribute('aria-current') === 'page' || link.getAttribute('aria-selected') === 'true');
                });
                if (index !== -1) return index;
                const currentScroll = track.scrollLeft;
                const trackPadding = parseFloat(getComputedStyle(track).paddingLeft) || 20;
                let closestIndex = 0;
                let minDiff = Infinity;
                slides.forEach((slide, i) => {
                    const diff = Math.abs(slide.offsetLeft - trackPadding - currentScroll);
                    if (diff < minDiff) { minDiff = diff; closestIndex = i; }
                });
                return closestIndex;
            };

            const scrollToSlide = (index) => {
                if (index < 0 || index >= slides.length) return;
                const trackPadding = parseFloat(getComputedStyle(track).paddingLeft) || 20;
                const targetSlide = slides[index];
                if (!targetSlide) return;
                const targetScroll = Math.round(targetSlide.offsetLeft - trackPadding);
                updateActiveState(index);
                smoothScrollTo(targetScroll);
            };

            const moveToSlide = (direction) => {
                if (!isActive()) return;
                const currentIndex = currentTargetIndex !== -1 ? currentTargetIndex : getActiveIndex();
                let targetIndex = direction === 'next' ? Math.min(currentIndex + 1, slides.length - 1) : Math.max(currentIndex - 1, 0);
                if (targetIndex === currentTargetIndex) return;
                currentTargetIndex = targetIndex;
                scrollToSlide(targetIndex);
            };

            slides.forEach((slide, index) => {
                slide.addEventListener('click', (e) => {
                    if (!e.isTrusted || window.matchMedia('(min-width: 1200px)').matches) return;
                    scrollToSlide(index);
                });
            });

            if (nextButton) nextButton.addEventListener('click', () => moveToSlide('next'));
            if (prevButton) prevButton.addEventListener('click', () => moveToSlide('prev'));

            // Dragging Support
            let isDown = false;
            let startX, scrollLeftPos, hasMoved = false;

            track.addEventListener('mousedown', (e) => {
                if (window.innerWidth >= 1200 || !isActive()) return;
                clearAnimating();
                isDown = true;
                hasMoved = false;
                track.classList.add('is-dragging');
                startX = e.pageX - track.offsetLeft;
                scrollLeftPos = track.scrollLeft;
                track.style.scrollSnapType = 'none';
                track.style.scrollBehavior = 'auto';
            });

            const stopDragging = () => {
                if (!isDown) return;
                isDown = false;
                track.classList.remove('is-dragging');
                track.style.scrollSnapType = '';
                track.style.scrollBehavior = '';
            };

            track.addEventListener('mouseup', stopDragging);
            track.addEventListener('mouseleave', stopDragging);
            track.addEventListener('mousemove', (e) => {
                if (!isDown || !isActive()) return;
                e.preventDefault();
                const x = e.pageX - track.offsetLeft;
                const walk = (x - startX) * 2;
                if (Math.abs(walk) > 5) hasMoved = true;
                track.scrollLeft = scrollLeftPos - walk;
            });

            track.addEventListener('click', (e) => {
                if (hasMoved) { e.preventDefault(); e.stopImmediatePropagation(); }
            }, true);
        }
    };

    const carousels = document.querySelectorAll('[data-js="carousel"]');
    carousels.forEach(initCarousel);
});